import { Grid, Table, TableCell, TableHead, TableRow } from "@material-ui/core";
import React, { useEffect } from "react";
import { ArrayInput, Button, CloneButton, Confirm, Create, Datagrid, DeleteWithConfirmButton, Edit, EditButton, Filter, FormDataConsumer, FunctionField, ImageField, ImageInput, Labeled, List, minValue, NumberField, NumberInput, RadioButtonGroupInput, required, SaveButton, SelectInput, Show, ShowButton, SimpleForm, SimpleFormIterator, SimpleShowLayout, TextField, TextInput, Toolbar, TopToolbar, useNotify, useRecordContext, BooleanInput } from "react-admin";
import { Link } from "react-router-dom";
import { ColorInput } from "react-admin-color-input";
import { useForm, useFormState } from "react-final-form";
import { PopupboxContainer } from "react-popupbox";
import dataProvider from "../../dataProvider";
import { MyDateField } from "../../MyDateField";
import { MyDateTimeInput } from "../../MyDateTimeInput";
import { ImageFieldWithRemove } from "../component/image-field-with-remove";
import { PlatformCheckboxGroupInput, PlatformChipField } from "../component/platform-components";
import { QrCodeEntryCheckboxGroupInput, QrCodeEntryChipField } from "../component/qr-code-entry-components";
import { CannedMessageList, CannedMessageListShow } from "./components/canned-message-list";
import { EpisodeSearch } from "./components/episode-search";
import { IconList, IconListShow } from "./components/icon-list";
import { ShowProductDialogContent } from "./components/product-dialog";
import { ProductList, ProductListShow } from "./components/product-list";
import { ShoppingCartModeCheckboxGroupInput, ShoppingCartModeChipField } from "./components/shopping-cart-mode-checkbox";

const StatusField = (props) => {
  const record = useRecordContext();

  var style = {};
  if (record.status == "live") {
    style = { color: "red" };
  }

  return <TextField {...props} source="status" style={style} />;
};

const StartNowButton = () => {
  const [showConfirm, setShowConfirm] = React.useState(false);
  const record = useRecordContext();
  const notify = useNotify();

  return (
    <React.Fragment>
      <Button
        label="Start Now"
        disabled={["live", "end"].includes(record.status)}
        onClick={() => {
          setShowConfirm(true);
        }}
      />
      <Confirm
        isOpen={showConfirm}
        title="Are you sure you want to start this chat room now?"
        onConfirm={async () => {
          try {
            await dataProvider.emergencyStartEndChatroom(record.id, true, false);
            window.location.reload(true);
          } catch (error) {
            notify(error.message.toString(), "warning");
          }

          setShowConfirm(false);
        }}
        onClose={() => {
          setShowConfirm(false);
        }}
      />
    </React.Fragment>
  );
};

const EndNowButton = () => {
  const [showConfirm, setShowConfirm] = React.useState(false);
  const record = useRecordContext();
  const notify = useNotify();

  return (
    <React.Fragment>
      <Button
        label="End Now"
        disabled={record.status != "live"}
        onClick={() => {
          setShowConfirm(true);
        }}
      />
      <Confirm
        isOpen={showConfirm}
        title="Are you sure you want to end this chat room now?"
        onConfirm={async () => {
          try {
            await dataProvider.emergencyStartEndChatroom(record.id, false, true);
            window.location.reload(true);
          } catch (error) {
            notify(error.message.toString(), "warning");
          }

          setShowConfirm(false);
        }}
        onClose={() => {
          setShowConfirm(false);
        }}
      />
    </React.Fragment>
  );
};

const CustomDelete = ({ record }) => {
  if (record.status != "preview") {
    return <> </>;
  }

  return <DeleteWithConfirmButton record={record} undoable={false} confirmTitle={"Delete " + record.name} />;
};

const IMChatroomFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Chat Room ID" source="id" alwaysOn />
    <TextInput label="Name" source="name" alwaysOn />
    <TextInput label="Channel No" source="channel_no" alwaysOn />
    <SelectInput
      label="Status"
      source="status"
      choices={[
        { id: "live", name: "Live" },
        { id: "preview", name: "Preview" },
        { id: "end", name: "End" },
      ]}
      alwaysOn
    />
  </Filter>
);

const ListActions = () => (
  <TopToolbar>
    <Button
      color="primary"
      component={Link}
      to={{
        pathname: `/im-rooms/create`,
      }}
      label="+ CREATE"
    />
  </TopToolbar>
);

export const IMChatroomList = (props) => (
  <List {...props} sort={{ field: "created_at", order: "DESC" }} bulkActionButtons={false} filters={<IMChatroomFilter />} actions={<ListActions />}>
    <Datagrid>
      <TextField source="id" label="Chat Room ID" />
      <TextField source="name" label="Name" />
      <TextField source="channel_no" label="Channel No" />
      <StatusField label="Status" />
      <MyDateField source="start_time" showTime />
      <MyDateField source="end_time" showTime />
      <StartNowButton />
      <EndNowButton />
      <ShowButton />
      <EditButton />
      <CloneButton />
      {/* <CustomEdit /> */}
      <CustomDelete />
    </Datagrid>
  </List>
);

const IMChatroomTitle = ({ record }) => {
  return <span>Chat Room {record ? `${record.id}` : ""}</span>;
};

const defaultChannelsPopupMode = [{ channel_no: null, popup_mode: 2 }];

const ChatroomEditForm = (props) => {
  const { record } = props;
  const form = useForm();

  const validatePopularityGrowthFactor = (values, allValues) => {
    if (!isNaN(allValues.pop_min_growth_factor) && !isNaN(allValues.pop_max_growth_factor) && allValues.pop_min_growth_factor > allValues.pop_max_growth_factor) {
      return "Max must be greater than or equal to Min";
    }

    return undefined;
  };

  const validatePopularityReductionCoefficient = (values, allValues) => {
    if (!isNaN(allValues.pop_min_reduction_coefficient) && !isNaN(allValues.pop_max_reduction_coefficient) && allValues.pop_min_reduction_coefficient > allValues.pop_max_reduction_coefficient) {
      return "Max must be greater than or equal to Min";
    }

    return undefined;
  };

  const validateLikesGrowthFactor = (values, allValues) => {
    if (!isNaN(allValues.like_min_growth_factor) && !isNaN(allValues.like_max_growth_factor) && allValues.like_min_growth_factor > allValues.like_max_growth_factor) {
      return "Max must be greater than or equal to Min";
    }

    return undefined;
  };

  const validateLikesReductionCoefficient = (values, allValues) => {
    if (!isNaN(allValues.like_min_reduction_coefficient) && !isNaN(allValues.like_max_reduction_coefficient) && allValues.like_min_reduction_coefficient > allValues.like_max_reduction_coefficient) {
      return "Max must be greater than or equal to Min";
    }

    return undefined;
  };

  const validateIsNumber = (values) => {
    return isNaN(values) ? "Must be a number" : undefined;
  };

  const validateNoDuplicates = (values) => {
    if (!values || !Array.isArray(values)) {
      return undefined; // No validation needed if values are not an array
    }

    const seen = new Set();
    for (const item of values) {
      if (item && item.channel_no) {
        if (seen.has(item.channel_no)) {
          return "Channel numbers must be unique.";
        }
        seen.add(item.channel_no);
      }
    }

    return undefined; // No duplicates found
  };

  const disableRemove = () => {
    if (["end"].includes(record.status)) {
      return true;
    }

    //NOTE: Maybe buggy, fields always empty set, so , use form instead
    //https://github.com/marmelab/react-admin/issues/5553
    const formData = form.getState().values;
    if (formData.channels_popup_mode.length <= 1) {
      return true;
    }

    return false;
  };

  const isAddDisabled = ["end"].includes(record.status);

  return (
    <>
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={6}>
          <TextInput source="name" validate={required()} fullWidth disabled={["end"].includes(record.status)} />
        </Grid>
        {/* <Grid item xs={12} sm={12} md={6}>
          <TextInput label="Channel Number" source="channel_no" validate={required()} fullWidth disabled={["live", "end"].includes(record.status)} />
        </Grid> */}
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={12}>
          <ArrayInput source="channels_popup_mode" label="Channels Popup Mode" validate={validateNoDuplicates} initialValue={record.channels_popup_mode && record.channels_popup_mode.length > 0 ? undefined : defaultChannelsPopupMode} disabled={["end"].includes(record.status)}>
            <SimpleFormIterator disableReordering={true} getItemLabel={() => ""} disableRemove={disableRemove} disableAdd={isAddDisabled}>
              {/* If want custom layout: https://stackoverflow.com/questions/60175609/arrayinput-with-simpleformiterator-of-react-admin-generate-wrong-output */}
              <FormDataConsumer>
                {({ getSource, scopedFormData }) => {
                  return (
                    <Grid container spacing={1} fullWidth>
                      <Grid item xs={12} sm={12} md={4}>
                        <NumberInput //
                          label="Channel Number"
                          source={getSource("channel_no")}
                          record={scopedFormData}
                          validate={required()}
                          disabled={["end"].includes(record.status)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={8}>
                        <RadioButtonGroupInput
                          fullWidth
                          label="Open Chatroom Mode"
                          source={getSource("popup_mode")}
                          record={scopedFormData}
                          validate={required()}
                          defaultValue={2}
                          disabled={["end"].includes(record.status)}
                          choices={[
                            { id: 0, name: "No prompt" },
                            { id: 1, name: "Prompt" },
                            { id: 2, name: "Direct to chatroom" },
                          ]}
                        />
                      </Grid>
                    </Grid>
                  );
                }}
              </FormDataConsumer>
            </SimpleFormIterator>
          </ArrayInput>
        </Grid>
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={6}>
          <MyDateTimeInput label="Start Time" source="start_time" fullWidth validate={required()} disabled={["live", "end"].includes(record.status)} />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <MyDateTimeInput label="End Time" source="end_time" fullWidth validate={required()} disabled={["end"].includes(record.status)} />
        </Grid>
      </Grid>
      <FormDataConsumer>
        {({ formData }) => (
          <>
            <EpisodeSearch formData={formData} canRemoveProgramme={!record.status || ["preview", "live"].includes(record.status)} />
          </>
        )}
      </FormDataConsumer>
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={12}>
          <PlatformCheckboxGroupInput useDefaultValue={true} disabled={["live", "end"].includes(record.status)} />
        </Grid>
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={12}>
          <QrCodeEntryCheckboxGroupInput disabled={["live", "end"].includes(record.status)} />
        </Grid>
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={4}>
          <NumberInput label="Initial Popularity" source="initial_popularity" fullWidth min={0} validate={minValue(0)} disabled={["live", "end"].includes(record.status)} />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <NumberInput label="Minimum growth factor" source="pop_min_growth_factor" fullWidth validate={validatePopularityGrowthFactor} disabled={["end"].includes(record.status)} />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <NumberInput label="Maximum growth factor" source="pop_max_growth_factor" fullWidth validate={validatePopularityGrowthFactor} disabled={["end"].includes(record.status)} />
        </Grid>
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={4}>
          <></>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <NumberInput label="Minimum reduction coefficient" source="pop_min_reduction_coefficient" fullWidth validate={validatePopularityReductionCoefficient} disabled={["end"].includes(record.status)} />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <NumberInput label="Maximum reduction coefficient" source="pop_max_reduction_coefficient" fullWidth validate={validatePopularityReductionCoefficient} disabled={["end"].includes(record.status)} />
        </Grid>
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={4}>
          <NumberInput label="Initial Likes" source="initial_likes" fullWidth min={0} validate={minValue(0)} disabled={["live", "end"].includes(record.status)} />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <NumberInput label="Minimum growth factor" source="like_min_growth_factor" fullWidth validate={validateLikesGrowthFactor} disabled={["end"].includes(record.status)} />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <NumberInput label="Maximum growth factor" source="like_max_growth_factor" fullWidth validate={validateLikesGrowthFactor} disabled={["end"].includes(record.status)} />
        </Grid>
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={4}>
          <></>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <NumberInput label="Minimum reduction coefficient" source="like_min_reduction_coefficient" fullWidth validate={validateLikesReductionCoefficient} disabled={["end"].includes(record.status)} />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <NumberInput label="Maximum reduction coefficient" source="like_max_reduction_coefficient" fullWidth validate={validateLikesReductionCoefficient} disabled={["end"].includes(record.status)} />
        </Grid>
      </Grid>

      {/* Chatroom Color Code */}
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={8}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={6}>
              {["end"].includes(record.status) ? (
                <Labeled label="Chatroom message color code">
                  <TextField source="chat_message_color_code" style={{ backgroundColor: "#4e4e4e", color: record.chat_message_color_code }} />
                </Labeled>
              ) : (
                <ColorInput className="MuiFormControl-fullWidth MuiFilledInput-root" source="chat_message_color_code" label="Chatroom message color code" validate={required()} isRequired={true} initialValue={record.chat_message_color_code ?? "#ffffff"} />
              )}
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              {["end"].includes(record.status) ? (
                <Labeled label="Chatroom background color code">
                  <TextField source="chat_background_color_code" style={{ backgroundColor: "#4e4e4e", color: record.chat_background_color_code }} />
                </Labeled>
              ) : (
                <ColorInput className="MuiFormControl-fullWidth MuiFilledInput-root" source="chat_background_color_code" label="Chatroom background color code" validate={required()} isRequired={true} initialValue={record.chat_background_color_code ?? "#000000"} />
              )}
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              {["end"].includes(record.status) ? (
                <Labeled label="Chatroom username color code">
                  <TextField source="username_color_code" style={{ backgroundColor: "#4e4e4e", color: record.username_color_code }} />
                </Labeled>
              ) : (
                <ColorInput className="MuiFormControl-fullWidth MuiFilledInput-root" source="username_color_code" label="Chatroom username color code" validate={required()} isRequired={true} initialValue={record.username_color_code ?? "#ff9800"} />
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <MessageColorPreview />
        </Grid>
      </Grid>

      {/* Artist Color Code */}
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={8}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={6}>
              {["end"].includes(record.status) ? (
                <Labeled label="Artist message color code">
                  <TextField source="artist_message_color_code" style={{ backgroundColor: "#4e4e4e", color: record.artist_message_color_code }} />
                </Labeled>
              ) : (
                <ColorInput className="MuiFormControl-fullWidth MuiFilledInput-root" source="artist_message_color_code" label="Artist message color code" validate={required()} isRequired={true} initialValue={record.artist_message_color_code ?? "#060f12"} />
              )}
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              {["end"].includes(record.status) ? (
                <Labeled label="Artist message background color code">
                  <TextField source="artist_message_background_color_code" style={{ backgroundColor: "#4e4e4e", color: record.artist_message_background_color_code }} />
                </Labeled>
              ) : (
                <ColorInput className="MuiFormControl-fullWidth MuiFilledInput-root" source="artist_message_background_color_code" label="Artist message background color code" validate={required()} isRequired={true} initialValue={record.artist_message_background_color_code ?? "#dbad00"} />
              )}
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              {["end"].includes(record.status) ? (
                <Labeled label="Artist name color code">
                  <TextField source="artist_username_color_code" style={{ backgroundColor: "#4e4e4e", color: record.artist_username_color_code }} />
                </Labeled>
              ) : (
                <ColorInput className="MuiFormControl-fullWidth MuiFilledInput-root" source="artist_username_color_code" label="Artist name color code" validate={required()} isRequired={true} initialValue={record.artist_username_color_code ?? "#ffffff"} />
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12} md={4}></Grid>
      </Grid>
      {/* DTT Control */}
      <Grid container spacing={1} fullWidth>
        <Labeled label="Is Artist Only">
          <BooleanInput label="Is Artist Only" source="send_dtt_config.artist_only" />
        </Labeled>
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Labeled label="Allow Public User Canned Message">
          <BooleanInput label="Allow Public User Canned Message" source="send_dtt_config.allow_user_canned_message" />
        </Labeled>
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Labeled label="Allow Public User Free Message">
          <BooleanInput label="Allow Public User Free Message" source="send_dtt_config.allow_user_free_message" />
        </Labeled>
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={4}>
          <NumberInput label="DTT Messages Per Batch Limit" source="send_dtt_config.response_message_limit" validate={validateIsNumber} fullWidth disabled={["end"].includes(record.status)} />
        </Grid>
      </Grid>
      <Grid container spacing={1} fullWidth> 
        <Grid item xs={12} sm={12} md={4}>
          <NumberInput label="DTT Artist Message Char Limit" source="send_dtt_config.artist_message_char_limit" validate={validateIsNumber} fullWidth disabled={["end"].includes(record.status)} />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <NumberInput label="DTT Special Message Char Limit" source="send_dtt_config.special_message_char_limit" validate={validateIsNumber} fullWidth disabled={["end"].includes(record.status)} />
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <NumberInput label="DTT Public Message Char Limit" source="send_dtt_config.public_message_char_limit" validate={validateIsNumber} fullWidth disabled={["end"].includes(record.status)} />
        </Grid>
      </Grid>
      {/* Chatroom Layout */}
      <Grid container spacing={1} fullWidth>
        <Grid item sm={12} md={12}>
          <RadioButtonGroupInput
            fullWidth
            label="Chatroom UI selection"
            source="layout"
            initialValue="default"
            validate={required()}
            choices={[
              { id: "default", name: "Original" },
              { id: "l-shape", name: "L - Shape" },
            ]}
          />
        </Grid>
      </Grid>

      <LShapeContent />

      {/* Canned Message */}
      <Grid container spacing={1} fullWidth>
        <Grid item sm={12} md={12}>
          <CannedMessageList chatroomId={record.id} roomStatus={record.status} {...props} />
        </Grid>
      </Grid>

      {/* Icon */}
      <Grid container spacing={1} fullWidth>
        <Grid item sm={12} md={12}>
          <IconList roomStatus={record.status} {...props} />
        </Grid>
      </Grid>

      {/* Shopping Cart Mode */}
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={12}>
          <ShoppingCartModeCheckboxGroupInput disabled={["end"].includes(record.status)} />
        </Grid>
      </Grid>

      {/* Product List*/}
      <Grid container spacing={1} fullWidth>
        <Grid item sm={12} md={12}>
          <ProductList chatroomId={record.id} roomStatus={record.status} {...props} />
        </Grid>
      </Grid>
    </>
  );
};

const LShapeContentShow = () => {
  const record = useRecordContext();

  if (record.layout != "l-shape") {
    return <></>;
  }

  return (
    <Grid container spacing={1} fullWidth>
      <Grid item xs={12} sm={12} md={12}>
        <Labeled label="L - Shape banner picture">
          <ImageField source="promote_banner" />
        </Labeled>
      </Grid>
      <Grid item sm={12} md={12}>
        <Labeled label="Promote URL">
          <TextField source="promote_url" label="Promote URL" fullWidth />
        </Labeled>
      </Grid>
      <Grid item sm={12} md={12}>
        <Labeled label="Promote message">
          <TextField source="promote_message" label="Promote Message" multiline fullWidth />
        </Labeled>
      </Grid>
      <Grid item sm={12} md={6}>
        <Labeled label="Promote message color code">
          <TextField source="promote_message_color_code" style={{ backgroundColor: "#4e4e4e", color: record.promote_message_color_code }} />
        </Labeled>
      </Grid>
      <Grid item sm={12} md={6}>
        <Labeled label="Promote message background color code">
          <TextField source="promote_message_background_color_code" style={{ backgroundColor: "#4e4e4e", color: record.promote_message_background_color_code }} />
        </Labeled>
      </Grid>
    </Grid>
  );
};

const validatePromoteBanner = (value, allValues) => {
  const hasExisting = allValues?.promote_banner; // existing image URL/field
  const upload = allValues?.promote_banner_base64;

  if (!hasExisting && !upload) {
    return "Required";
  }
  return undefined;
};

const LShapeContent = () => {
  const form = useForm();
  const record = useRecordContext();
  const { values } = useFormState();

  if (values.layout != "l-shape") {
    return <></>;
  }

  return (
    <Grid container spacing={1} fullWidth>
      <Grid item xs={12} sm={12} md={12}>
        <ImageFieldWithRemove
          id="promote_banner"
          source="promote_banner"
          fullWidth
          onRemove={() => {
            form.change("promote_banner", null);
            form.change("promote_banner_base64", null);
          }}
        />
        {/* <ImageField id="promote_banner" source="promote_banner" fullWidth /> */}
        <ImageInput source="promote_banner_base64" label="L - Shape Banner Picture (With promote QR Code: 1340 x 166; without promote QR code: 1519 x 166) *" accept="image/*" validate={validatePromoteBanner}>
          <ImageField source="src" title="title" />
        </ImageInput>
      </Grid>
      <Grid item sm={12} md={12}>
        <TextInput source="promote_url" label="Promote URL" fullWidth />
      </Grid>
      <Grid item sm={12} md={12}>
        <TextInput source="promote_message" label="Promote Text" validate={required()} multiline fullWidth />
      </Grid>
      <Grid item sm={12} md={4}>
        <ColorInput className="MuiFormControl-fullWidth MuiFilledInput-root" source="promote_message_color_code" label="Promote message color code" validate={required()} isRequired={true} initialValue={record.promote_message_color_code ?? "#ffffff"} />
      </Grid>
      <Grid item sm={12} md={4}>
        <ColorInput className="MuiFormControl-fullWidth MuiFilledInput-root" source="promote_message_background_color_code" label="Promote message background color code" validate={required()} isRequired={true} initialValue={record.promote_message_background_color_code ?? "#0e2678"} />
      </Grid>
      <Grid item sm={12} md={4}>
        <PromoteMessgaePreview />
      </Grid>
    </Grid>
  );
};
const PromoteMessgaePreview = () => {
  const { values } = useFormState();

  const promoteMessageColorCode = values.promote_message_color_code;
  const promoteBackgroundColorCode = values.promote_message_background_color_code;

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", background: promoteBackgroundColorCode, padding: 8, borderRadius: 8 }}>
        <span style={{ fontSize: "1.2em", fontWeight: 500, color: promoteMessageColorCode }}>Promote Text</span>
      </div>
    </>
  );
};
const MessageColorPreview = () => {
  const { values } = useFormState();

  const chatMessageColorCode = values.chat_message_color_code;
  const chatBackgroundColorCode = values.chat_background_color_code;
  const chatUsernameColor = values.username_color_code;

  const artistMessageColorCode = values.artist_message_color_code;
  const artistBackgroundColorCode = values.artist_message_background_color_code;
  const artistUsernameColor = values.artist_username_color_code;

  return (
    <div style={{ background: "rgba(0,0,0,0.7)", padding: 8, borderRadius: 8 }}>
      <div style={{ display: "flex", alignItems: "center", background: artistBackgroundColorCode, padding: 8, borderRadius: 8 }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "#bbb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: 16,
          }}
        />
        <span style={{ fontSize: "1.2em", fontWeight: 500, color: artistUsernameColor }}>Artist name</span>
        &nbsp;
        <span style={{ fontSize: "1.2em", fontWeight: 500, color: artistMessageColorCode }}>Artist message</span>
      </div>
      <div style={{ height: 8 }}></div>
      <div style={{ display: "flex", alignItems: "center", background: chatBackgroundColorCode, padding: 8, borderRadius: 8 }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "#bbb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: 16,
          }}
        />
        <span style={{ fontSize: "1.2em", fontWeight: 500, color: chatUsernameColor }}>Username</span>
        &nbsp;
        <span style={{ fontSize: "1.2em", fontWeight: 500, color: chatMessageColorCode }}>Artist message</span>
      </div>
    </div>
  );
};

const ChatroomShowForm = (props) => {
  const { record } = props;

  const ProductListSummary = ({ products, ...props }) => {
    return (
      <Grid container spacing={1} fullWidth>
        <Grid item sm={12} md={12}>
          <Labeled label="Product List">
            <>
              {products.map((item) => {
                return <ShowProductDialogContent item={item} {...props} />;
              })}
            </>
          </Labeled>
        </Grid>
      </Grid>
    );
  };
  const RoomSummaryReport = ({ summary }) => {
    return (
      <>
        <Grid container spacing={1} fullWidth>
          <Grid item sm={12} md={12}>
            <Labeled label="Statistic Report">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Actual (Accumulate unique count)</TableCell>
                    <TableCell>Display (Peak)</TableCell>
                  </TableRow>
                </TableHead>
                <TableRow>
                  <TableCell style={{ overflowWrap: "break-word" }}>Popularity</TableCell>
                  <TableCell style={{ overflowWrap: "break-word" }}>{summary.actual_num_of_users}</TableCell>
                  <TableCell style={{ overflowWrap: "break-word" }}>{summary.display_num_of_users}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ overflowWrap: "break-word" }}>Likes</TableCell>
                  <TableCell style={{ overflowWrap: "break-word" }}>{summary.actual_num_of_likes}</TableCell>
                  <TableCell style={{ overflowWrap: "break-word" }}>{summary.display_num_of_likes}</TableCell>
                </TableRow>
              </Table>
            </Labeled>
          </Grid>
        </Grid>
        <Grid container spacing={1} fullWidth>
          <Grid item sm={12} md={12}>
            <Labeled label="Canned Message Click Rate">
              <>
                {summary.canned_message_stats?.map((set) => {
                  return (
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Set of Name</TableCell>
                          <TableCell>Voting</TableCell>
                          <TableCell>Voting Question</TableCell>
                          <TableCell>Canned Message</TableCell>
                          <TableCell>Unique Click Rate</TableCell>
                          <TableCell>Total Click Rate</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableRow>
                        <TableCell rowSpan={set.canned_messages.length + 1}>{set.name}</TableCell>

                        <TableCell rowSpan={set.canned_messages.length + 1}>
                          {set.list_type == "vote" ? (
                            <svg className="MuiSvgIcon-root MuiSvgIcon-fontSizeSmall" focusable="false" viewBox="0 0 24 24" aria-hidden="true" data-testid="true">
                              <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"></path>
                            </svg>
                          ) : (
                            <svg className="MuiSvgIcon-root MuiSvgIcon-fontSizeSmall" focusable="false" viewBox="0 0 24 24" aria-hidden="true" data-testid="false">
                              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
                            </svg>
                          )}
                        </TableCell>

                        <TableCell rowSpan={set.canned_messages.length + 1}>{set.question}</TableCell>
                      </TableRow>
                      {set.canned_messages.map((item) => {
                        return (
                          <TableRow>
                            <TableCell style={{ overflowWrap: "break-word" }}>{item.text}</TableCell>
                            <TableCell style={{ overflowWrap: "break-word" }}>{item.unique_count}</TableCell>
                            <TableCell style={{ overflowWrap: "break-word" }}>{item.total_count}</TableCell>
                          </TableRow>
                        );
                      })}
                    </Table>
                  );
                })}
              </>
            </Labeled>
          </Grid>
        </Grid>
        <Grid container spacing={1} fullWidth>
          <Grid item sm={12} md={12}>
            <Labeled label="Icon Click Rate">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Icon</TableCell>
                    <TableCell>Click Rate</TableCell>
                  </TableRow>
                </TableHead>

                {record.icon_summaries?.map((item) => {
                  return (
                    <TableRow>
                      <TableCell style={{ overflowWrap: "break-word" }}>
                        <img src={item.image_url} style={{ height: "30px", width: "30px" }} />
                      </TableCell>
                      <TableCell style={{ overflowWrap: "break-word" }}>{item.count}</TableCell>
                    </TableRow>
                  );
                })}
              </Table>
            </Labeled>
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <SimpleShowLayout>
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="ID">
            <TextField source="id" />
          </Labeled>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Name">
            <TextField source="name" />
          </Labeled>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Channel Number">
            <TextField source="channel_no" />
          </Labeled>
        </Grid>
      </Grid>

      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={6}>
          <Labeled label="Start Time">
            <MyDateField showTime source="start_time" />
          </Labeled>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Labeled label="End Time">
            <MyDateField showTime source="end_time" />
          </Labeled>
        </Grid>
      </Grid>

      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Programme ID">
            <TextField source="programme_id" />
          </Labeled>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Name in TC">
            <TextField source="caption_tc" />
          </Labeled>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Name in EN">
            <TextField source="caption_en" />
          </Labeled>
        </Grid>
      </Grid>

      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={12}>
          <Labeled label="Episode No">
            <TextField source="episode_no" />
          </Labeled>
        </Grid>
      </Grid>

      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={12}>
          <Labeled label="Platform">
            <PlatformChipField />
          </Labeled>
        </Grid>
      </Grid>

      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={12}>
          <Labeled label="QR Code Entry">
            <QrCodeEntryChipField />
          </Labeled>
        </Grid>
      </Grid>

      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Initial Popularity">
            <NumberField source="initial_popularity" />
          </Labeled>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Minimum growth factor">
            <NumberField source="pop_min_growth_factor" />
          </Labeled>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Maximum growth factor">
            <NumberField source="pop_max_growth_factor" />
          </Labeled>
        </Grid>
      </Grid>

      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={4}>
          <></>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Minimum reduction coefficient">
            <NumberField source="pop_min_reduction_coefficient" />
          </Labeled>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Maximum reduction coefficient">
            <NumberField source="pop_max_reduction_coefficient" />
          </Labeled>
        </Grid>
      </Grid>

      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Initial Likes">
            <NumberField source="initial_likes" />
          </Labeled>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Minimum growth factor">
            <NumberField source="like_min_growth_factor" />
          </Labeled>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Maximum growth factor">
            <NumberField source="like_max_growth_factor" />
          </Labeled>
        </Grid>
      </Grid>

      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={4}>
          <></>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Minimum reduction coefficient">
            <NumberField source="like_min_reduction_coefficient" />
          </Labeled>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Maximum reduction coefficient">
            <NumberField source="like_max_reduction_coefficient" />
          </Labeled>
        </Grid>
      </Grid>

      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Chatroom message color code">
            <TextField source="chat_message_color_code" style={{ backgroundColor: "#4e4e4e", color: record.chat_message_color_code }} />
          </Labeled>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Chatroom background color code">
            <TextField source="chat_background_color_code" style={{ backgroundColor: "#4e4e4e", color: record.chat_background_color_code }} />
          </Labeled>
        </Grid>
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Chatroom customer name color code">
            <TextField source="username_color_code" style={{ backgroundColor: "#4e4e4e", color: record.username_color_code }} />
          </Labeled>
        </Grid>
      </Grid>

      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Artist message color code">
            <TextField source="artist_message_color_code" style={{ backgroundColor: "#4e4e4e", color: record.artist_message_color_code }} />
          </Labeled>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Artist message background color code">
            <TextField source="artist_message_background_color_code" style={{ backgroundColor: "#4e4e4e", color: record.artist_message_background_color_code }} />
          </Labeled>
        </Grid>
      </Grid>
      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Artist name color code">
            <TextField source="artist_username_color_code" style={{ backgroundColor: "#4e4e4e", color: record.artist_username_color_code }} />
          </Labeled>
        </Grid>
      </Grid>

      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={4}>
          <Labeled label="Chatroom UI selection">
            <FunctionField source="layout" render={(record) => (record.layout == "l-shape" ? "L - Shape" : "Original")} />
          </Labeled>
        </Grid>
      </Grid>

      <LShapeContentShow />

      <Grid container spacing={1} fullWidth>
        <Grid item sm={12} md={12}>
          <CannedMessageListShow source="canned_message_list" {...props} />
        </Grid>
      </Grid>

      <Grid container spacing={1} fullWidth>
        <Grid item sm={12} md={12}>
          <IconListShow source="icon_list" {...props} />
        </Grid>
      </Grid>

      <Grid container spacing={1} fullWidth>
        <Grid item sm={12} md={12}>
          <ProductListShow source="products" {...props} />
        </Grid>
      </Grid>

      {record.room_summaries && Object.keys(record.room_summaries).length > 0 && <RoomSummaryReport summary={record.room_summaries} />}

      <Grid container spacing={1} fullWidth>
        <Grid item xs={12} sm={12} md={12}>
          <Labeled label="Shopping Cart Mode">
            <ShoppingCartModeChipField />
          </Labeled>
        </Grid>
      </Grid>
      {record.products && Object.keys(record.products).length > 0 && <ProductListSummary products={record.products} />}
    </SimpleShowLayout>
  );
};

const redirect = (_basePath, id) => {
  const newUrl = `/im-rooms/${id}`;

  // Check if the current URL matches the redirect URL
  if (window.location.hash.indexOf(newUrl) !== -1) {
    window.location.reload(); // Force reload if it's the same page, because returning to the same page doesn't trigger a reload
  }

  return newUrl; // Redirect to the new URL
};

const CustomToolbar = (props) => (
  <Toolbar
    {...props}
    style={{
      display: "flex",
      gap: "16px", // Add spacing between buttons
    }}
  >
    <SaveButton label="SAVE" redirect={redirect} />
    <SaveButton label="SAVE AND EXIT" />
  </Toolbar>
);

export const IMChatroomEdit = (props) => {
  const notify = useNotify();
  const onFailure = (error) => {
    notify(error.message.toString(), "warning");
  };

  return (
    <>
      <Edit {...props} onFailure={onFailure} mutationMode="pessimistic" title={<IMChatroomTitle />}>
        <SimpleForm redirect={redirectIMChatroom} toolbar={<CustomToolbar />}>
          <ChatroomEditForm />
        </SimpleForm>
      </Edit>
      <PopupboxContainer />
    </>
  );
};

const redirectIMChatroom = () => "/im-rooms";

export const IMChatroomCreate = (props) => {
  const notify = useNotify();
  const onFailure = (error) => {
    notify(error.message.toString(), "warning");
  };

  return (
    <>
      <Create {...props} onFailure={onFailure}>
        <SimpleForm toolbar={<CustomToolbar />} redirect={redirectIMChatroom}>
          <ChatroomCreateForm />
        </SimpleForm>
      </Create>
      <PopupboxContainer />
    </>
  );
};

const ChatroomCreateForm = ({ record, ...props }) => {
  const form = useForm();
  const [x, setX] = React.useState(record);

  //Note: In clone. After save, `record` field reset to original, we don't want all the changes reset, so, we need to clone it to another variable
  useEffect(() => {
    if (record) {
      delete record.created_at;
      delete record.modified_at;
      delete record.status;

      //Each im-room should have only one icon_list, Move icon_list to anothe variable
      if (record.icon_list) {
        if (record.icon_list.length > 0) {
          var iconList = record.icon_list[0];
          form.change("icons", iconList.icons); //Clone
        }

        delete record.icon_list;
      }

      //Add canned_mesasge status for convinient
      if (record.canned_message_list) {
        for (const item of record.canned_message_list) {
          item.status = "preview"; //Change status to preview
        }

        form.change("canned_message_list", record.canned_message_list);

        delete record.canned_message_list;
      }

      //Move stats_config out 1 level
      for (const key in record["stats_config"]) {
        record[key] = record["stats_config"][key];
      }

      delete record.stats_config;

      setX(record);
    }
  }, []);

  return <ChatroomEditForm record={x} {...props} />;
};

export const IMChatroomShow = (props) => {
  return (
    <>
      <Show {...props} title={<IMChatroomTitle />}>
        <ChatroomShowForm />
      </Show>
      <PopupboxContainer />
    </>
  );
};
